// Imports
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { UnverifiedBankAccount } from '.';
import { Stripe } from '@stripe/stripe-js';
import Input from '../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Button from '../../../../../../shareables/foundation/front-end/components/buttons/standard';
import FieldErrorMessage from '../../../../../../shareables/foundation/front-end/components/forms/field-error-message';
import Modal from '../../../../../../shareables/front-end/modal';
import modalHelpers from '../../../../../../shareables/front-end/modal/modal-helpers';
import { darken, transparentize } from 'polished';
import { GeneratedThemeProps } from '../../../../../templates/template-style';
import { fieldContainerBottomMargin } from '../../../../../../shareables/foundation/front-end/components/forms/field-container';
import currency from 'currency.js';
import commonActions from '../../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import * as Sentry from '@sentry/browser';


// Styled components
const Instruction = styled.div`
	padding-bottom: ${fieldContainerBottomMargin};
`;

const InputWrapper = styled.div`
	& > div:first-child {
		margin-bottom: 0 !important;
	}
	
	padding-bottom: ${fieldContainerBottomMargin};
`;

const Verify = styled(Button)<{ theme: GeneratedThemeProps }>`
	background-color: ${(props) => props.theme.baseColor};
	
	&:hover,
	&:focus {
		background-color: ${(props) => darken(0.05, props.theme.baseColor)} !important;
	}
	
	&:focus {
		box-shadow: ${(props) => `
					0 0.125em 0.25em rgba(0, 0, 0, 0.05),
					0 0 0 0.125em #fff,
					0 0 0 0.25em ${transparentize(0.75, darken(0.05, props.theme.baseColor))} !important;
				`};
	}
`;


// Define the accepted props
interface Props {
	paymentMethod: UnverifiedBankAccount | null;
	stripeAccountID: string;
	onVerificationCompletion: () => void;
	stripePromise: Promise<Stripe | null>;
	currencySymbol: string;
}


// Function component
const VerifyBankAccountModal: React.FC<Props> = ({
	paymentMethod,
	stripeAccountID,
	onVerificationCompletion,
	stripePromise,
	currencySymbol,
}) => {
	// Use state
	const [firstAmount, setFirstAmount] = useState(0);
	const [secondAmount, setSecondAmount] = useState(0);
	const [descriptorCode, setDescriptorCode] = useState('');
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState<{
		firstAmount?: string;
		secondAmount?: string;
		descriptorCode?: string;
	}>({});
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Reset state when payment method changes
	useEffect(() => {
		if (!paymentMethod) {
			setFirstAmount(0);
			setSecondAmount(0);
			setDescriptorCode('');
			setLoading(false);
			setErrors({});
		}
	}, [paymentMethod]);
	
	
	// Function that validates inputs in case browser validation is bypassed
	const validateInputs = () => {
		// Initialize
		const newErrors: typeof errors = {};
		
		
		// Helper function to validate amounts
		const validateAmount = (value: number, fieldName: keyof typeof errors) => {
			if (!Number.isFinite(value) || value <= 0) {
				newErrors[fieldName] = 'Value must be greater than or equal to 0.01';
			}
			
			if (value >= 1) {
				newErrors[fieldName] = 'Value must be less than or equal to 0.99';
			}
		};
		
		
		// Handle amounts
		if (paymentMethod?.verification.microdeposit === 'amounts') {
			validateAmount(firstAmount, 'firstAmount');
			validateAmount(secondAmount, 'secondAmount');
			return newErrors;
		}
		
		
		// Handle descriptor code
		if (!descriptorCode.trim()) {
			newErrors.descriptorCode = 'This is required.';
		}
		
		return newErrors;
	};
	
	
	// Handle form submission
	const handleVerify = async (event: React.FormEvent) => {
		// Prevent the form's default submission behavior
		if (event) {
			event.preventDefault();
		}
		
		
		// Update state
		setErrors({});
		setLoading(true);
		
		
		// Wrap in a try/catch
		try {
			// Handle missing info
			if (!paymentMethod) {
				throw new Error('Missing payment method');
			}
			
			if (!stripeAccountID) {
				throw new Error('Missing Stripe account ID');
			}
			
			
			// Resolve promise
			const stripe = await stripePromise;
			
			
			// Handle if Stripe.js didn't load for some reason
			if (!stripe) {
				throw new Error('Stripe.js hasn’t loaded');
			}
			
			
			// Validate inputs
			const validationErrors = validateInputs();
			
			if (Object.keys(validationErrors).length > 0) {
				setErrors(validationErrors);
				setLoading(false);
				return;
			}
			
			
			// Attempt to verify
			const { error: verifyError } = await stripe.verifyMicrodepositsForSetup(
				paymentMethod.verification.setupIntentClientSecret,
				paymentMethod.verification.microdeposit === 'amounts'
					? { amounts: [currency(firstAmount).intValue, currency(secondAmount).intValue] }
					: { descriptor_code: descriptorCode.trim().toUpperCase() }
			);
			
			
			// Handle errors
			if (verifyError) {
				// Initialize
				const errorMessage = verifyError.message ?? '';
				
				
				// Handle the verification code not matching
				if (
					paymentMethod.verification.microdeposit === 'code' &&
					(verifyError.code === 'payment_method_microdeposit_verification_descriptor_code_mismatch' ||
						errorMessage
							.toLowerCase()
							.includes('the verification code provided does not match the one sent to the bank account'))
				) {
					// Display as inline error
					setErrors((previousErrors) => ({
						...previousErrors,
						descriptorCode: errorMessage,
					}));
					
					
					// Return
					return;
				}
				
				
				// Handle the amounts not matching
				if (
					paymentMethod.verification.microdeposit === 'amounts' &&
					(verifyError.code === 'payment_method_microdeposit_verification_amounts_mismatch' ||
						verifyError.code === 'payment_method_microdeposit_verification_amounts_invalid' ||
						errorMessage
							.toLowerCase()
							.includes('amounts provided do not match the amounts that were sent to the bank account') ||
						errorMessage.toLowerCase().includes('you must provide two different micro-deposit amounts'))
				) {
					// Display as page message
					dispatch(
						commonActions.showPageMessage({
							color: 'danger',
							title: 'Oh snap!',
							message: errorMessage.replace(/micro-deposit/gi, 'microdeposit'),
						})
					);
					
					
					// Return
					return;
				}
				
				
				// Handle exceeding the allowed number of verification attempts
				if (
					verifyError.code === 'payment_method_microdeposit_verification_attempts_exceeded' ||
					errorMessage.toLowerCase().includes('you have exceeded the number of allowed verification attempts')
				) {
					// Close the modal and reload payment methods
					//  -> The payment method will have been detached after exceeding attempts
					onVerificationCompletion();
					modalHelpers.close('verify-bank-account-modal');
					
					
					// Display the error
					dispatch(
						commonActions.showPageMessage({
							color: 'danger',
							title: 'Oh snap!',
							message: errorMessage,
						})
					);
					
					
					// Return
					return;
				}
				
				
				// Otherwise treat it as unexpected
				throw new Error(errorMessage);
			}
			
			
			// Handle success
			onVerificationCompletion();
			modalHelpers.close('verify-bank-account-modal');
			dispatch(
				commonActions.showPageMessage({
					color: 'success',
					title: 'Success!',
					message: 'Your bank account has been verified. It’s now ready for use.',
				})
			);
		} catch (error: unknown) {
			// Handle unexpected errors
			Sentry.captureException(error);
			
			dispatch(
				commonActions.showPageMessage({
					color: 'danger',
					title: 'Oh snap!',
					message: 'There was an unexpected error.',
				})
			);
		} finally {
			// Reset loading state
			setLoading(false);
		}
	};
	
	
	// Handle input changes
	const handleInputChange =
		<T,>(
			setter: React.Dispatch<React.SetStateAction<T>>,
			inputName: keyof typeof errors,
			parser?: (value: string) => T
		) =>
		(newValue: string) => {
			// Update state
			setter(parser ? parser(newValue) : (newValue as unknown as T));
			
			
			// Reset errors
			setErrors((previousErrors) => {
				if (!previousErrors[inputName]) {
					return previousErrors;
				}
				
				return {
					...previousErrors,
					[inputName]: undefined,
				};
			});
		};
	
	
	// Return JSX
	return (
		<Modal identifier='verify-bank-account' title='Verify bank account'>
			<form
				onSubmit={(event) => {
					void handleVerify(event);
				}}
			>
				{paymentMethod?.verification.microdeposit === 'amounts' ? (
					<React.Fragment>
						<Instruction>
							We recently sent two microdeposits to this bank account. They typically take 1-2 business days to arrive.
							There will be two amounts, each less than $1. To verify this bank account, please provide the two amounts
							below. (Order does not matter.)
						</Instruction>
						
						<Input
							key={`${paymentMethod?.id}-first-amount`}
							type='number'
							name='firstAmount'
							label='First amount'
							prefix={currencySymbol}
							min={0.01}
							max={0.99}
							step={0.01}
							onChange={handleInputChange<number>(setFirstAmount, 'firstAmount', parseFloat)}
							required
						/>
						{errors.firstAmount && <FieldErrorMessage message={errors.firstAmount} />}
						
						<Input
							key={`${paymentMethod?.id}-second-amount`}
							type='number'
							name='secondAmount'
							label='Second amount'
							prefix={currencySymbol}
							min={0.01}
							max={0.99}
							step={0.01}
							onChange={handleInputChange<number>(setSecondAmount, 'secondAmount', parseFloat)}
							required
						/>
						{errors.secondAmount && <FieldErrorMessage message={errors.secondAmount} />}
					</React.Fragment>
				) : (
					<React.Fragment>
						<Instruction>
							We recently sent a 1¢ microdeposit to this bank account. It typically takes 1-2 business days to arrive.
							The microdeposit’s descriptor contains a 6-character code starting with <strong>SM</strong>. To verify
							this bank account, please provide the code below.
						</Instruction>
						
						<InputWrapper>
							<Input
								type='text'
								name='descriptorCode'
								label='Descriptor code'
								placeholder='SM12AB'
								onChange={handleInputChange<string>(setDescriptorCode, 'descriptorCode')}
								disabled={loading}
								key={paymentMethod?.id}
								required
								maxLength={6}
								pattern='[Ss][Mm][0-9A-Za-z]{4}'
							/>
							{errors.descriptorCode && <FieldErrorMessage message={errors.descriptorCode} />}
						</InputWrapper>
					</React.Fragment>
				)}
				
				<div>
					<Verify type='submit' isLoading={loading}>
						Verify
					</Verify>
				</div>
			</form>
		</Modal>
	);
};

export default VerifyBankAccountModal;
